/**
 * DecisionFlow — KillSwitchManager
 * ui/src/components/tenant-admin/KillSwitchManager.jsx
 * S565 — Tenant-level agent kill switch management
 *
 * Surface: TenantAdminShell — "Agent Kill Switch" screen
 * RBAC:    tenant_super_admin only
 *
 * Endpoints:
 *   GET  /api/v1/agent/kill-switch            — status + history
 *   POST /api/v1/agent/kill-switch/activate   — activate with reason
 *   POST /api/v1/agent/kill-switch/deactivate — deactivate with reason
 *
 * Design rules (non-negotiable):
 *   CSS: rgb(var(--color-*)) only
 *   No hardcoded hex colors
 *   Text sizes: text-tok-*
 *   Tables: max-h-* + overflow-y-auto + sticky thead
 *   Modal does NOT close on outside click (NN-07)
 */

import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, ShieldOff, ShieldCheck, Clock, XCircle } from "lucide-react";
import { useAuth } from "../../auth/AuthContext.jsx";
import Button from "../shared/Button.jsx";
import Card from "../shared/Card.jsx";

const API_BASE = "/api/v1/agent";
const REASON_MIN = 10;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function truncateId(id) {
  if (!id) return "—";
  return id.slice(0, 8) + "…";
}

// ---------------------------------------------------------------------------
// ConfirmModal — activate or deactivate with required reason
// Does NOT close on outside click (NN-07)
// ---------------------------------------------------------------------------

function ConfirmModal({ mode, onConfirm, onCancel, saving }) {
  const [reason, setReason] = useState("");
  const isActivate = mode === "activate";
  const canSubmit  = reason.trim().length >= REASON_MIN && !saving;

  const tone = isActivate
    ? "bg-[rgb(var(--color-danger))]/8 border-[rgb(var(--color-danger))]/30"
    : "bg-[rgb(var(--color-success))]/8 border-[rgb(var(--color-success))]/30";

  const btnCls = isActivate
    ? "bg-[rgb(var(--color-danger))] hover:opacity-90 text-white"
    : "bg-[rgb(var(--color-success))] hover:opacity-90 text-white";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-6 shadow-xl">

        <div className={`mb-4 flex items-start gap-3 rounded-xl border px-4 py-3 ${tone}`}>
          {isActivate
            ? <ShieldOff  className="h-5 w-5 shrink-0 text-[rgb(var(--color-danger))] mt-0.5" />
            : <ShieldCheck className="h-5 w-5 shrink-0 text-[rgb(var(--color-success))] mt-0.5" />
          }
          <div>
            <p className="text-tok-sm font-bold text-[rgb(var(--color-text))]">
              {isActivate ? "Activate agent kill switch?" : "Deactivate agent kill switch?"}
            </p>
            <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
              {isActivate
                ? "All AI agent activity for this tenant will be immediately halted. Comp admins will see a 'Agent unavailable' message. You can reactivate at any time."
                : "Agent activity will resume immediately for all users on this tenant."
              }
            </p>
          </div>
        </div>

        <div className="mb-4 space-y-1">
          <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
            Reason <span className="text-[rgb(var(--color-danger))]">*</span>
          </label>
          <textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              isActivate
                ? "e.g. Pausing AI during Q3 internal audit per compliance requirement…"
                : "e.g. Audit complete — resuming normal agent operations…"
            }
            className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))] resize-none"
          />
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            {reason.trim().length}/{REASON_MIN} characters minimum · stored in audit trail
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            disabled={!canSubmit}
            className={`rounded-xl px-4 py-2 text-tok-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-opacity ${btnCls}`}
          >
            {saving
              ? (isActivate ? "Activating…" : "Deactivating…")
              : (isActivate ? "Activate Kill Switch" : "Deactivate Kill Switch")
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatusBanner — large prominent status display
// ---------------------------------------------------------------------------

function StatusBanner({ isActive, platformBlocked, activeEntry }) {
  if (platformBlocked) {
    return (
      <div className="rounded-xl border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/8 px-4 py-4">
        <div className="flex items-start gap-3">
          <ShieldOff className="h-5 w-5 shrink-0 text-[rgb(var(--color-danger))] mt-0.5" />
          <div>
            <p className="text-tok-sm font-bold text-[rgb(var(--color-danger))]">
              Platform-level kill switch active
            </p>
            <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
              data-fit has activated a platform-level restriction. Agent activity is halted
              across all tenants. Contact data-fit support for status.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isActive && activeEntry) {
    return (
      <div className="rounded-xl border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/8 px-4 py-4">
        <div className="flex items-start gap-3">
          <ShieldOff className="h-5 w-5 shrink-0 text-[rgb(var(--color-danger))] mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-tok-sm font-bold text-[rgb(var(--color-danger))]">
              Agent kill switch is ACTIVE — all AI activity halted
            </p>
            <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
              Reason: <span className="text-[rgb(var(--color-text))]">{activeEntry.reason}</span>
            </p>
            <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
              Activated: {formatDate(activeEntry.created_at)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[rgb(var(--color-success))]/40 bg-[rgb(var(--color-success))]/8 px-4 py-4">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-5 w-5 shrink-0 text-[rgb(var(--color-success))]" />
        <div>
          <p className="text-tok-sm font-bold text-[rgb(var(--color-success))]">
            No active restrictions — agent operating normally
          </p>
          <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
            All AI agent features are available to users on this tenant.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KillSwitchManager root
// ---------------------------------------------------------------------------

export default function KillSwitchManager() {
  const { user } = useAuth();

  const [status,   setStatus]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [modal,    setModal]    = useState(null);  // 'activate' | 'deactivate' | null
  const [saving,   setSaving]   = useState(false);
  const [toast,    setToast]    = useState(null);

  // ── Load status ────────────────────────────────────────────────────────────

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res  = await fetch(`${API_BASE}/kill-switch`, { credentials: "include" });
      const body = await res.json();
      if (!res.ok) {
        const code = body?.error?.code ?? "LOAD_ERROR";
        throw new Error(`[${code}] ${body?.error?.message ?? `HTTP ${res.status}`}`);
      }
      setStatus(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Activate / deactivate ──────────────────────────────────────────────────

  async function handleConfirm(reason) {
    setSaving(true);
    const endpoint = modal === "activate"
      ? `${API_BASE}/kill-switch/activate`
      : `${API_BASE}/kill-switch/deactivate`;

    try {
      const res  = await fetch(endpoint, {
        method:      "POST",
        credentials: "include",
        headers:     { "Content-Type": "application/json" },
        body:        JSON.stringify({ reason }),
      });
      const body = await res.json();
      if (!res.ok) {
        const code = body?.error?.code ?? "ACTION_ERROR";
        throw new Error(`[${code}] ${body?.error?.message ?? `HTTP ${res.status}`}`);
      }
      setModal(null);
      setToast(body.message ?? (modal === "activate" ? "Kill switch activated." : "Kill switch deactivated."));
      await loadStatus();
    } catch (e) {
      setToast(`Error: ${e.message}`);
      setModal(null);
    } finally {
      setSaving(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* Toast */}
      {toast && (
        <div className="fixed right-5 top-5 z-50 max-w-sm rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 shadow-lg text-tok-sm text-[rgb(var(--color-text))]">
          {toast}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <ConfirmModal
          mode={modal}
          onConfirm={handleConfirm}
          onCancel={() => setModal(null)}
          saving={saving}
        />
      )}

      {/* Status card */}
      <Card title="Agent Kill Switch">
        <p className="mb-4 text-tok-xs text-[rgb(var(--color-text-muted))]">
          Immediately halt or resume all AI agent activity for this tenant.
          Activation reason is required and stored in the audit trail.
          This does not affect non-AI features.
        </p>

        {loading && (
          <div className="animate-pulse space-y-2">
            <div className="h-16 rounded-xl skeleton" />
          </div>
        )}

        {error && !loading && (
          <div className="rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/8 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
            <p className="font-semibold">Unable to load kill switch status</p>
            <p className="mt-1">{error}</p>
            <button
              type="button"
              onClick={loadStatus}
              className="mt-2 text-tok-xs underline underline-offset-2 hover:opacity-80"
            >
              Retry
            </button>
          </div>
        )}

        {status && !loading && (
          <>
            <StatusBanner
              isActive={status.is_active}
              platformBlocked={status.platform_blocked}
              activeEntry={status.active_entry}
            />

            {/* Action button */}
            {!status.platform_blocked && (
              <div className="mt-4">
                {status.is_active ? (
                  <button
                    type="button"
                    onClick={() => setModal("deactivate")}
                    className="inline-flex items-center gap-2 rounded-xl border border-[rgb(var(--color-success))]/40 bg-[rgb(var(--color-success))]/8 px-4 py-2.5 text-tok-sm font-semibold text-[rgb(var(--color-success))] hover:bg-[rgb(var(--color-success))]/15 transition-colors"
                  >
                    <ShieldCheck className="h-4 w-4" />
                    Deactivate Kill Switch — Resume Agent
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setModal("activate")}
                    className="inline-flex items-center gap-2 rounded-xl border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/8 px-4 py-2.5 text-tok-sm font-semibold text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/15 transition-colors"
                  >
                    <ShieldOff className="h-4 w-4" />
                    Activate Kill Switch — Halt Agent
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </Card>

      {/* History table */}
      {status?.history?.length > 0 && (
        <Card title="Activation History">
          <p className="mb-3 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Complete audit trail of kill switch activations and deactivations for this tenant.
          </p>
          <div className="max-h-80 overflow-y-auto rounded-xl border border-[rgb(var(--color-border))]">
            <table className="w-full text-tok-sm">
              <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))]">
                <tr>
                  <th className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Status</th>
                  <th className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Reason</th>
                  <th className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Activated</th>
                  <th className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">Deactivated</th>
                  <th className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgb(var(--color-border))]">
                {status.history.map((entry) => (
                  <tr key={entry.kill_switch_id} className="bg-[rgb(var(--color-surface))] hover:bg-[rgb(var(--color-surface-muted))]">
                    <td className="px-3 py-2.5">
                      {entry.is_active ? (
                        <span className="inline-flex items-center gap-1 text-tok-xs font-semibold text-[rgb(var(--color-danger))]">
                          <ShieldOff className="h-3.5 w-3.5" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Resolved
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-[rgb(var(--color-text))] max-w-xs truncate" title={entry.reason}>
                      {entry.reason}
                    </td>
                    <td className="px-3 py-2.5 text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                      {formatDate(entry.created_at)}
                    </td>
                    <td className="px-3 py-2.5 text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                      {entry.deactivated_at ? formatDate(entry.deactivated_at) : "—"}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-tok-xs text-[rgb(var(--color-text-muted))]">
                      {truncateId(entry.kill_switch_id)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
